import type { IsoDate } from './common.ts';
import type { SourceRef } from './source.ts';

/** Indice ATMO (arrêté du 10 juillet 2020) : 1 Bon … 6 Extrêmement mauvais. */
export type AirQualityLevel = 1 | 2 | 3 | 4 | 5 | 6;

export type Pollutant = 'no2' | 'pm10' | 'pm25' | 'o3' | 'so2';

export interface PollutantReading {
  readonly pollutant: Pollutant;
  readonly level?: AirQualityLevel;
  readonly concentrationUgM3?: number;
}

export interface AirQualityObservation {
  /** L'indice ATMO est journalier. */
  readonly validOn: IsoDate;
  readonly isForecast: boolean;
  readonly level: AirQualityLevel;
  readonly pollutants: readonly PollutantReading[];
  readonly responsiblePollutants: readonly Pollutant[];
  readonly source: SourceRef;
}

/**
 * Libellés officiels confirmés (docs/02 §3.1).
 * Couleurs des niveaux 1 à 4 confirmées sur le flux Atmo France (`coul_qual`, 2026-09-18) ;
 * niveaux 5 et 6 : notice de l'indice ATMO, aucune occurrence récente dans le flux — TO VERIFY.
 */
export const AIR_QUALITY_LEVELS: Readonly<Record<AirQualityLevel, { label: string; color: string }>> = {
  1: { label: 'Bon', color: '#50F0E6' },
  2: { label: 'Moyen', color: '#50CCAA' },
  3: { label: 'Dégradé', color: '#F0E641' },
  4: { label: 'Mauvais', color: '#FF5050' },
  5: { label: 'Très mauvais', color: '#960032' },
  6: { label: 'Extrêmement mauvais', color: '#872181' },
};

export const POLLUTANT_LABELS: Readonly<Record<Pollutant, string>> = {
  no2: 'Dioxyde d’azote (NO₂)',
  pm10: 'Particules PM10',
  pm25: 'Particules fines PM2,5',
  o3: 'Ozone (O₃)',
  so2: 'Dioxyde de soufre (SO₂)',
};

export function isAirQualityLevel(value: number): value is AirQualityLevel {
  return Number.isInteger(value) && value >= 1 && value <= 6;
}
